import React, { useState, useEffect } from "react";
import { Mars, Venus, Image } from "lucide-react";
import {
    nationalities,
    weightClasses,
    fighterEnties,
} from "../constants";
import { Timestamp } from "firebase/firestore";
import { calculateAge } from "../utils/common";
import Preview from "./preview";
import toast from "react-hot-toast";

export default function FighterAccountForm({ formData, setFormData, errors }) {
    const [birthdateString, setBirthdateString] = useState("");

    // Sync birthdateString with formData.birthdate
    useEffect(() => {
        if (formData.birthdate && formData.birthdate.toDate) {
            const date = formData.birthdate.toDate();
            setBirthdateString(date.toISOString().split("T")[0]);
        } else if (!formData.birthdate) {
            setBirthdateString("");
        }
    }, [formData.birthdate]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        switch (name) {
            case "birthdate":
                setBirthdateString(value);
                if (value) {
                    const date = new Date(value);
                    if (!isNaN(date.getTime())) {
                        const age = calculateAge(date);
                        setFormData((prev) => ({
                            ...prev,
                            birthdate: Timestamp.fromDate(date),
                            age: age,
                        }));
                    }
                }
                break;
            case "age":
                setFormData((prev) => {
                    const now = new Date();
                    const newYear = now.getFullYear() - parseInt(value) || 0;
                    const d = prev?.birthdate?.toDate() ?? new Date();
                    d.setFullYear(newYear);
                    return {
                        ...prev,
                        age: value,
                        birthdate: Timestamp.fromDate(d),
                    };
                });
                break;
            default:
                setFormData((prev) => ({
                    ...prev,
                    [name]: value,
                }));
                break;
        }
    };

    const handleRecordChange = (entry, key, value) => {
        setFormData((prev) => ({
            ...prev,
            records: {
                ...prev.records,
                [entry]: {
                    ...prev.records[entry],
                    [key]: parseInt(value) || 0,
                },
            },
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) {
            setFormData((prev) => ({
                ...prev,
                fighterPicture: null,
            }));
            return;
        }

        // Get file extension
        const fileName = file.name;
        const fileExt = fileName.split(".").pop().toLowerCase();

        // List of allowed extensions
        const allowedExts = ["jpg", "jpeg", "png", "webp"];

        if (!allowedExts.includes(fileExt)) {
            toast.error(
                `Please upload a valid image type: ${allowedExts.join(", ")}`
            );
            e.target.value = "";
            return;
        }

        // Check file size (max 25MB)
        const maxSize = 25 * 1024 * 1024; // 25MB
        if (file.size > maxSize) {
            toast.error("File size too large (max 25MB)");
            e.target.value = "";
            return;
        }

        // Process the valid file
        setFormData((prev) => ({
            ...prev,
            fighterPicture: file,
        }));
    };

    return (
        <div className="space-y-4">
            {/* Basic Information */}
            <div className="border-2 border-base-content p-4">
                <h3 className="text-lg font-bold mb-4 border-b-2 border-base-content pb-2">
                    BASIC INFORMATION
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="label">
                            <span className="label-text font-bold">Name *</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name || ""}
                            onChange={handleChange}
                            className={`input input-bordered w-full ${
                                errors.name ? "input-error" : ""
                            }`}
                            placeholder="Full Name"
                        />
                        {errors.name && (
                            <label className="label">
                                <span className="label-text-alt text-error">
                                    {errors.name}
                                </span>
                            </label>
                        )}
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                Email *
                            </span>
                        </label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email || ""}
                            onChange={handleChange}
                            className={`input input-bordered w-full ${
                                errors.email ? "input-error" : ""
                            }`}
                            placeholder="email@example.com"
                        />
                        {errors.email && (
                            <label className="label">
                                <span className="label-text-alt text-error">
                                    {errors.email}
                                </span>
                            </label>
                        )}
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                Phone Number *
                            </span>
                        </label>
                        <input
                            type="tel"
                            name="phoneNumber"
                            value={formData.phoneNumber || ""}
                            onChange={handleChange}
                            className={`input input-bordered w-full ${
                                errors.phoneNumber ? "input-error" : ""
                            }`}
                            placeholder="+60123456789"
                        />
                        {errors.phoneNumber && (
                            <label className="label">
                                <span className="label-text-alt text-error">
                                    {errors.phoneNumber}
                                </span>
                            </label>
                        )}
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                Nationality
                            </span>
                        </label>
                        <select
                            name="nationality"
                            value={formData.nationality || "Malaysian"}
                            onChange={handleChange}
                            className="select select-bordered w-full"
                        >
                            {nationalities.map((nationality) => (
                                <option key={nationality} value={nationality}>
                                    {nationality}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Personal Details */}
            <div className="border-2 border-base-content p-4">
                <h3 className="text-lg font-bold mb-4 border-b-2 border-base-content pb-2">
                    PERSONAL DETAILS
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                Birthdate
                            </span>
                        </label>
                        <input
                            type="date"
                            name="birthdate"
                            value={birthdateString}
                            onChange={handleChange}
                            max={new Date().toISOString().split("T")[0]}
                            min="1900-01-01"
                            className="input input-bordered w-full"
                        />
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text font-bold">Age</span>
                        </label>
                        <input
                            type="number"
                            name="age"
                            value={formData.age || ""}
                            onChange={handleChange}
                            className="input input-bordered w-full"
                            placeholder="Age"
                        />
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text font-bold">Gender</span>
                        </label>
                        <select
                            name="gender"
                            value={formData.gender || ""}
                            onChange={handleChange}
                            className={`select select-bordered w-full ${
                                errors.gender ? "select-error" : ""
                            }`}
                        >
                            <option value="">Select Gender</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                        </select>
                        {errors.gender && (
                            <label className="label">
                                <span className="label-text-alt text-error">
                                    {errors.gender}
                                </span>
                            </label>
                        )}
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                Weight Class
                            </span>
                        </label>
                        <select
                            name="weightClass"
                            value={formData.weightClass || ""}
                            onChange={handleChange}
                            className="select select-bordered w-full"
                        >
                            <option value="">Select Weight Class</option>
                            {Object.keys(weightClasses).map((key) => (
                                <option key={key} value={key}>
                                    {key} - {weightClasses[key]}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                Height (cm)
                            </span>
                        </label>
                        <input
                            type="number"
                            name="height"
                            value={formData.height || ""}
                            onChange={handleChange}
                            className="input input-bordered w-full"
                            placeholder="Height in cm"
                        />
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                IC / Passport
                            </span>
                        </label>
                        <input
                            type="text"
                            name="icOrPassport"
                            value={formData.icOrPassport || ""}
                            onChange={handleChange}
                            className="input input-bordered w-full"
                            placeholder="IC or Passport Number"
                        />
                    </div>
                </div>
            </div>

            {/* Contact & Social */}
            <div className="border-2 border-base-content p-4">
                <h3 className="text-lg font-bold mb-4 border-b-2 border-base-content pb-2">
                    CONTACT & SOCIAL
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                Emergency Contact
                            </span>
                        </label>
                        <input
                            type="tel"
                            name="emergencyContactNumber"
                            value={formData.emergencyContactNumber || ""}
                            onChange={handleChange}
                            className="input input-bordered w-full"
                            placeholder="+60123456789"
                        />
                    </div>

                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                Instagram Username
                            </span>
                        </label>
                        <input
                            type="text"
                            name="igUsername"
                            value={formData.igUsername || ""}
                            onChange={handleChange}
                            className="input input-bordered w-full"
                            placeholder="@username"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="label">
                            <span className="label-text font-bold">
                                Gym Name / Team Name
                            </span>
                        </label>
                        <input
                            type="text"
                            name="gymNameOrTeamName"
                            value={formData.gymNameOrTeamName || ""}
                            onChange={handleChange}
                            className="input input-bordered w-full"
                            placeholder="Gym or Team Name"
                        />
                    </div>

                    <div className="md:col-span-2">
                        <label className="label">
                            <span className="label-text font-bold">
                                Walk Out Music
                            </span>
                        </label>
                        <input
                            type="text"
                            name="walkOutMusic"
                            value={formData.walkOutMusic || ""}
                            onChange={handleChange}
                            className="input input-bordered w-full"
                            placeholder="Song title and artist"
                        />
                    </div>
                </div>
            </div>

            {/* Records */}
            <div className="border-2 border-base-content p-4">
                <h3 className="text-lg font-bold mb-4 border-b-2 border-base-content pb-2">
                    FIGHT RECORDS
                </h3>
                <div className="overflow-x-auto">
                    <table className="table table-bordered w-full">
                        <thead>
                            <tr>
                                <th>Entry</th>
                                <th>Win</th>
                                <th>Loss</th>
                                <th>Draw</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fighterEnties.map((entry) => (
                                <tr key={entry}>
                                    <td className="font-bold">{entry}</td>
                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            value={
                                                formData.records?.[entry]?.win ||
                                                0
                                            }
                                            onChange={(e) =>
                                                handleRecordChange(
                                                    entry,
                                                    "win",
                                                    e.target.value
                                                )
                                            }
                                            className="input input-bordered input-sm w-full"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            value={
                                                formData.records?.[entry]
                                                    ?.loss || 0
                                            }
                                            onChange={(e) =>
                                                handleRecordChange(
                                                    entry,
                                                    "loss",
                                                    e.target.value
                                                )
                                            }
                                            className="input input-bordered input-sm w-full"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            value={
                                                formData.records?.[entry]
                                                    ?.draw || 0
                                            }
                                            onChange={(e) =>
                                                handleRecordChange(
                                                    entry,
                                                    "draw",
                                                    e.target.value
                                                )
                                            }
                                            className="input input-bordered input-sm w-full"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Profile Picture */}
            <div className="border-2 border-base-content p-4">
                <h3 className="text-lg font-bold mb-4 border-b-2 border-base-content pb-2">
                    PROFILE PICTURE
                </h3>
                <div className="space-y-4">
                    <div>
                        <label className="label">
                            <span className="label-text font-bold">
                                Fighter Profile Picture
                            </span>
                            <span className="label-text-alt text-base-content/70">
                                (Optional)
                            </span>
                        </label>
                        <input
                            type="file"
                            name="fighterPicture"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleFileChange}
                            className="file-input file-input-bordered w-full"
                        />
                        <label className="label">
                            <span className="label-text-alt text-base-content/70">
                                Accepted formats: JPG, PNG, WEBP (Max 25MB)
                            </span>
                        </label>
                    </div>

                    {formData.fighterPicture && (
                        <div className="border-2 border-base-content p-4">
                            <label className="label">
                                <span className="label-text font-bold">
                                    Preview
                                </span>
                            </label>
                            <div className="w-full max-w-xs aspect-square border-2 border-base-content overflow-hidden">
                                <Preview
                                    src={formData.fighterPicture}
                                    className="w-full h-full"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
